<template>
    <div>
        <label class="ml-1 mr-3">
            <input
                name="rubric_level_zero"
                type="checkbox"
                v-model="rubric.rubric_level_zero"
                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded mr-3"
            />
            <span class="inline-block text-sm leading-5 font-medium text-gray-700">
                {{ $t('messages.rubric_level_zero') }}
            </span>
        </label>
    </div>
    <div class="bg-gray-100 overflow-hidden rounded-lg mt-3">
        <div class="px-4 py-5 sm:p-6">
            <div class="flex flex-col mt-5" v-if="rubric.subactivities.length > 0">
                <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                    <div
                        class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                    >
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead>
                                <tr>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 tracking-wider"
                                    >
                                        -
                                    </th>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        v-show="rubric.rubric_level_zero"
                                    >
                                        0
                                    </th>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        v-for="i in rubric.rubric_levels"
                                        :key="i"
                                    >
                                        {{ i }}
                                    </th>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider flex space-x-1 items-center"
                                    >
                                        <button
                                            @click="removeLevel()"
                                            type="button"
                                            class="inline-flex items-center border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        >
                                            <svg
                                                class="w-6 h-6"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    fill-rule="evenodd"
                                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z"
                                                    clip-rule="evenodd"
                                                ></path>
                                            </svg>
                                        </button>
                                        <PlusButton @click="addLevel()" v-if="rubric.rubric_levels < 10" />
                                    </th>
                                </tr>
                            </thead>
                            <thead v-for="subactivity in rubric.subactivities" :key="subactivity.id">
                                <tr>
                                    <th
                                        :title="subactivity.title"
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 tracking-wider"
                                    >
                                        {{ subactivity.name }}

                                        <PlusButton @click="addRow(subactivity)" />
                                    </th>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        :colspan="rubric.rubric_levels + (rubric.rubric_level_zero ? 1 : 0)"
                                    ></th>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        <span class="sr-only">{{ $t('messages.actions') }}</span>
                                    </th>
                                </tr>
                                <tr :class="'bg-white'" v-for="(line, lineIndex) in subactivity.lines" :key="lineIndex">
                                    <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                        <FormInput v-model="line.name" required />
                                    </td>
                                    <td
                                        v-for="level in line.levels"
                                        class="px-1 py-1 whitespace-nowrap text-sm leading-5 text-gray-500"
                                        :class="getLevelClasses(level, rubric.rubric_levels)"
                                        :key="`${lineIndex}_${level.level}`"
                                        v-show="level.level !== 0 || rubric.rubric_level_zero"
                                    >
                                        <FormTextarea
                                            class="md:text-xs"
                                            v-model="level.description"
                                            :class="getLevelClasses(level, rubric.rubric_levels)"
                                        />
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                        <button
                                            type="button"
                                            @click="deleteRow(subactivity, lineIndex)"
                                            class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            v-if="subactivity.lines.length > 1"
                                        >
                                            <svg viewBox="0 0 20 20" fill="currentColor" class="trash w-4 h-4">
                                                <path
                                                    fill-rule="evenodd"
                                                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                                    clip-rule="evenodd"
                                                ></path>
                                            </svg>
                                        </button>
                                    </td>
                                </tr>
                            </thead>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import FormInput from '@/components/Forms/FormInput.vue';
import FormTextarea from '@/components/Forms/FormTextarea.vue';
import PlusButton from '@/components/Forms/PlusButton.vue';
import GradeTranslator from '@/classes/GradeTranslator.js';
import { ref } from 'vue';

const props = defineProps({
    modelValue: {},
    ranges: {
        required: true,
    },
});

const rubric = ref(props.modelValue);

const gradeTranslator = new GradeTranslator(props.ranges);

const getLevelClasses = (level, rubricLevels) => {
    const grade = (level.level / rubricLevels) * 10;
    return gradeTranslator.scoreToPillClasses(grade);
};

function addRow(subactivity) {
    let levels = JSON.parse(JSON.stringify(subactivity.lines[0].levels));
    levels.forEach((l) => (l.description = ''));
    subactivity.lines.push({
        id: null,
        name: '',
        levels: levels,
    });
}

function deleteRow(subactivity, lineIndex) {
    subactivity.lines.splice(lineIndex, 1);
}

function addLevel() {
    let newLevel = rubric.value.rubric_levels + 1;
    rubric.value.subactivities.forEach((subactivity) => {
        subactivity.lines.forEach((line) => {
            line.levels.push({
                description: '',
                level: newLevel,
            });
        });
    });
    rubric.value.rubric_levels = newLevel;
}
function removeLevel() {
    rubric.value.subactivities.forEach((subactivity) => {
        subactivity.lines.forEach((line) => {
            line.levels.pop();
        });
    });
    rubric.value.rubric_levels--;
}
</script>
